import React, { useState, useContext } from 'react';
import { Box, Button, Typography, Paper, styled } from '@mui/material';
import { OrgContext } from '../contexts/OrgContext';
import { useNavigate } from 'react-router-dom';
import { bulkCreateCustomers } from '../services/customerService';
import Papa from 'papaparse';
import TanStackTable from '../components/TanStackTable';
import BackButton from '../components/BackButton';

const HiddenInput = styled('input')({
  display: 'none',
});

const CustomersBulkImport = () => {
  const { orgId } = useContext(OrgContext);
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [error, setError] = useState(null);
  const [results, setResults] = useState(null);

  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
  };

  const handleDownloadTemplate = () => {
    const csvContent = 'data:text/csv;charset=utf-8,'
      + 'customer_name,email,contact_number,whatsapp_number,address\n'
      + 'John Doe,john@example.com,+1234567890,+1234567890,"123 Main St, Anytown, USA"\n'
      + 'Jane Smith,jane@example.com,+0987654321,+0987654321,"456 Park Ave, Othertown, USA"\n'
      + 'Acme Corporation,contact@acme.com,+1122334455,+1122334455,"789 Corporate Blvd, Business City, USA"\n';
    
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', 'customers_template.csv');
    document.body.appendChild(link);
    link.click();
  };

  const handleUpload = () => {
    if (!file) {
      setError('Please select a file to upload.');
      return;
    }
    setError(null);

    Papa.parse(file, {
      header: true,
      complete: async (parseResults) => {
        const customers = parseResults.data.map((customer, index) => ({
          customer_name: customer.customer_name,
          email: customer.email,
          contact_number: customer.contact_number,
          address: customer.address,
          meta: { whatsapp_number: customer.whatsapp_number || undefined },
          org_id: parseInt(orgId, 10),
          row: index + 2, // Add row number for error tracking (1 for header, so start from 2)
        }));

        try {
          const response = await bulkCreateCustomers(customers);
          setResults(response);
        } catch (err) {
          setError('Error uploading customers.');
          console.error(err);
        }
      },
      error: (err, file, inputElem, reason) => {
        setError(`Error parsing CSV file at row ${err.row}: ${reason}`);
        console.error(err);
      },
    });
  };

  const handleDone = () => {
    navigate(`/${orgId}/customers`);
  };

  return (
    <Paper sx={{ p: 2, backgroundColor: '#1e1e1e', color: '#ffffff' }}>
      <BackButton />
      <Typography variant="h5" gutterBottom>
        Bulk Import Customers
      </Typography>
      <Typography variant="body1" gutterBottom>
        Follow the instructions below to bulk import customers:
      </Typography>
      <Typography variant="body2" gutterBottom>
        1. Download the template CSV file.
      </Typography>
      <Typography variant="body2" gutterBottom>
        2. Fill in the customer details in the CSV file. Ensure no extra rows are present, as they will lead to failures during the upload.
      </Typography>
      <Typography variant="body2" gutterBottom>
        3. Required fields: customer_name, contact_number.
      </Typography>
      <Typography variant="body2" gutterBottom>
        4. Optional fields: email, address.
      </Typography>
      <Typography variant="body2" gutterBottom>
        5. Upload the completed CSV file and start bulk import.
      </Typography>

      <Button variant="contained" onClick={handleDownloadTemplate} sx={{ my: 2, mr: 2 }}>
        Download Template CSV
      </Button>

      <label htmlFor="contained-button-file">
        <HiddenInput
          accept=".csv"
          id="contained-button-file"
          type="file"
          onChange={handleFileChange}
        />
        <Button variant="contained" component="span">
          Select CSV File
        </Button>
      </label>

      {file && (
        <Typography variant="body2" sx={{ mt: 2 }}>
          Selected File: {file.name}
        </Typography>
      )}

      <Box sx={{ mt: 2 }}>
        <Button variant="contained" onClick={handleUpload}>
          Start Bulk Import
        </Button>
      </Box>

      {error && (
        <Typography variant="body2" color="error" sx={{ mt: 2 }}>
          {error}
        </Typography>
      )}

      {results && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="h6" gutterBottom>
            Bulk Import Results
          </Typography>
          <Typography sx={{ mb: 3, color: "#ddd" }} variant="body2" gutterBottom>
            Scroll down to see the successful and failed imports.
          </Typography>
          <Typography variant="body2" gutterBottom>
            Successful Imports
          </Typography>
          <Box sx={{ mb: 2 }}>
            <TanStackTable
              data={results.successes || []}
              columns={[
                { header: 'ID', accessorKey: 'id' },
                { header: 'Customer Name', accessorKey: 'customer_name' },
                { header: 'Email', accessorKey: 'email' },
                { header: 'Contact Number', accessorKey: 'contact_number' },
                { header: 'Address', accessorKey: 'address' },
                { header: 'Whatsapp Number', id: 'whatsapp_number', accessorFn: (row) => row.meta?.whatsapp_number || '' },
              ]}
            />
          </Box>
          <Typography variant="body2" gutterBottom>
            Failed Imports
          </Typography>
          <Box>
            <TanStackTable
              data={(results.failures || []).map((f) => ({
                row: f.dto.row,
                customer_name: f.dto.customer_name || '',
                email: f.dto.email || '',
                contact_number: f.dto.contact_number || '',
                address: f.dto.address || '',
                whatsapp_number: f.dto.meta?.whatsapp_number || '',
                error: f.error,
              }))}
              columns={[
                { header: 'Row', accessorKey: 'row' },
                { header: 'Customer Name', accessorKey: 'customer_name' },
                { header: 'Email', accessorKey: 'email' },
                { header: 'Contact Number', accessorKey: 'contact_number' },
                { header: 'Address', accessorKey: 'address' },
                { header: 'Whatsapp Number', accessorKey: 'whatsapp_number' },
                { header: 'Error', accessorKey: 'error' },
              ]}
            />
          </Box>
          <Button variant="contained" onClick={handleDone} sx={{ mt: 2 }}>
            Done
          </Button>
        </Box>
      )}
    </Paper>
  );
};

export default CustomersBulkImport;
